import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import logo from "@/assets/goatbar-logo.png";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading: sessionLoading, signIn } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionLoading && user) {
      navigate({ to: "/gia", replace: true });
    }
  }, [sessionLoading, user, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await signIn(username.trim(), password);

    if (result.error) {
      setError(result.error);
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen w-full flex bg-background text-foreground">
      <div className="hidden lg:flex flex-col justify-between flex-1 relative overflow-hidden border-r border-border bg-sidebar">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, var(--primary) 0, transparent 45%), radial-gradient(circle at 70% 80%, var(--primary) 0, transparent 45%)",
          }}
        />
        <div className="relative px-14 pt-12">
          <div className="label-eyebrow">Goat Bar · Management System</div>
        </div>
        <div className="relative px-14 flex-1 flex items-center">
          <img src={logo} alt="GOAT BAR" className="w-72 h-auto" />
        </div>
        <div className="relative px-14 pb-12 max-w-lg">
          <p className="font-display text-2xl leading-snug font-medium">
            Hospitalidade premium,
            <br />
            <span className="text-muted-foreground">gestão de alta performance.</span>
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Eventos · 7Steakhouse · Goat Botequim
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative z-20 pointer-events-auto">
        <div className="w-full max-w-sm relative z-20 pointer-events-auto">
          <div className="flex lg:hidden items-center gap-3 mb-10">
            <img src={logo} alt="GOAT BAR" className="h-12 w-auto" />
          </div>

          <div className="label-eyebrow mb-3">Acesso interno</div>
          <h1 className="font-display text-3xl font-semibold leading-tight">Bem-vindo de volta</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Entre com seu usuário e senha para acessar o painel.
          </p>

          <form onSubmit={handleSubmit} autoComplete="off" className="mt-8 space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Usuário</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="w-full h-11 px-4 rounded-lg bg-surface border border-border focus:border-primary focus:outline-none text-sm transition-colors"
                placeholder="@romulochaves"
              />
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Senha</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full h-11 px-4 rounded-lg bg-surface border border-border focus:border-primary focus:outline-none text-sm transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>

          <div className="mt-5 text-center">
            <Link
              to="/primeiro-acesso"
              className="text-sm font-medium text-primary hover:underline"
            >
              Primeiro acesso / Esqueci minha senha
            </Link>
            <p className="mt-1 text-xs text-muted-foreground">
              Primeiro acesso ou esqueceu a senha? Solicite um código à administração.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
